import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

/**
 * useTodos — a person's own to-dos (`/todos/:userId`), loaded once and
 * kept in step with add, complete and delete. Lifted out of TodoDropdown so
 * the consultant's Queue can show the same list as a section (ledger L22:
 * the to-do dropdown was a sixth "what next" surface).
 */

export interface Todo {
  id: string;
  text: string;
  completed: boolean;
  createdAt: string;
  dueDate?: string;
}

export type TodoUrgency = 'overdue' | 'dueSoon' | 'normal';

/** Overdue, due within a day, or neither; completed to-dos are never urgent. */
export function todoUrgency(todo: Pick<Todo, 'dueDate' | 'completed'>, now: Date = new Date()): TodoUrgency {
  if (!todo.dueDate || todo.completed) return 'normal';
  const due = new Date(todo.dueDate);
  if (Number.isNaN(due.getTime())) return 'normal';
  const hours = (due.getTime() - now.getTime()) / 3_600_000;
  if (hours < 0) return 'overdue';
  if (hours <= 24) return 'dueSoon';
  return 'normal';
}

export interface UseTodosResult {
  todos: Todo[];
  pending: Todo[];
  completed: Todo[];
  loading: boolean;
  reload: () => Promise<void>;
  addTodo: (text: string, dueDate?: string) => Promise<boolean>;
  toggleTodo: (todoId: string, completed: boolean) => Promise<void>;
  deleteTodo: (todoId: string) => Promise<void>;
}

const base = (userId: string) => `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/todos/${userId}`;

export function useTodos(userId: string | undefined, accessToken: string | undefined, enabled = true): UseTodosResult {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(enabled);

  const headers = useCallback((json = false) => ({
    Authorization: `Bearer ${accessToken}`,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  }), [accessToken]);

  const reload = useCallback(async () => {
    if (!enabled || !userId || !accessToken) { setLoading(false); return; }
    try {
      setLoading(true);
      const response = await fetch(base(userId), { headers: headers() });
      if (response.ok) {
        const data = await response.json();
        setTodos(Array.isArray(data.todos) ? data.todos : []);
      }
    } catch (error) {
      console.error('[useTodos] load failed:', error);
    } finally {
      setLoading(false);
    }
  }, [enabled, userId, accessToken, headers]);

  useEffect(() => { void reload(); }, [reload]);

  const addTodo = useCallback(async (text: string, dueDate?: string) => {
    const trimmed = text.trim();
    if (!trimmed || !userId) return false;
    try {
      const response = await fetch(base(userId), {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({ text: trimmed, dueDate: dueDate || undefined }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setTodos((prev) => [data.todo, ...prev]);
      toast.success('To-do added');
      return true;
    } catch (error) {
      console.error('[useTodos] add failed:', error);
      toast.error('Could not add the to-do');
      return false;
    }
  }, [userId, headers]);

  const toggleTodo = useCallback(async (todoId: string, completed: boolean) => {
    if (!userId) return;
    try {
      const response = await fetch(`${base(userId)}/${todoId}`, {
        method: 'PATCH',
        headers: headers(true),
        body: JSON.stringify({ completed }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setTodos((prev) => prev.map((t) => (t.id === todoId ? { ...t, completed } : t)));
    } catch (error) {
      console.error('[useTodos] update failed:', error);
      toast.error('Could not update the to-do');
    }
  }, [userId, headers]);

  const deleteTodo = useCallback(async (todoId: string) => {
    if (!userId) return;
    try {
      const response = await fetch(`${base(userId)}/${todoId}`, { method: 'DELETE', headers: headers() });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setTodos((prev) => prev.filter((t) => t.id !== todoId));
      toast.success('To-do deleted');
    } catch (error) {
      console.error('[useTodos] delete failed:', error);
      toast.error('Could not delete the to-do');
    }
  }, [userId, headers]);

  return {
    todos,
    pending: todos.filter((t) => !t.completed),
    completed: todos.filter((t) => t.completed),
    loading,
    reload,
    addTodo,
    toggleTodo,
    deleteTodo,
  };
}
